import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface OSFamily {
  id: number;
  name: string;
  slug: string;
  description: string;
  icon: string;
}

interface OSProduct {
  id: number;
  name: string;
  manufacturer: string;
  family_name: string;
  family_id: number;
  major_version: number;
  is_supported: boolean;
  version_pattern: string;
}

export function OperatingSystems() {
  const [families, setFamilies] = useState<OSFamily[]>([]);
  const [allProducts, setAllProducts] = useState<OSProduct[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load families and all products in parallel
      const [familiesRes, productsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/os/families`),
        fetch(`${API_BASE_URL}/os/products`)
      ]);

      if (!familiesRes.ok || !productsRes.ok) throw new Error('Failed to fetch');

      const familiesData = await familiesRes.json();
      const productsData = await productsRes.json();

      setFamilies(familiesData);
      setAllProducts(productsData);
    } catch (err) {
      setError('Failed to load operating systems');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/os/products/${productId}`);
  };

  // Filter products based on selected family and search query
  const filteredProducts = allProducts.filter((product) => {
    const matchesFamily = selectedFamily === null || product.family_id === selectedFamily;
    const matchesSearch = searchQuery === '' ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.manufacturer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFamily && matchesSearch;
  });

  // Group products by family for the grid view
  const productsByFamily = filteredProducts.reduce((acc, product) => {
    if (!acc[product.family_name]) {
      acc[product.family_name] = [];
    }
    acc[product.family_name].push(product);
    return acc;
  }, {} as Record<string, OSProduct[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading operating systems...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getIcon = (iconName: string) => {
    const icons: Record<string, string> = {
      'windows': '🪟',
      'linux': '🐧',
      'apple': '🍎',
      'smartphone': '📱',
      'network': '🌐',
      'server': '🖥️',
    };
    return icons[iconName] || '💻';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Operating Systems</h1>
          <p className="text-lg text-gray-600">
            Track and manage operating system versions across your infrastructure
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Operating Systems
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  id="search"
                  type="text"
                  placeholder="Search by name or manufacturer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Family Filters */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Family
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedFamily(null)}
                  className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                    selectedFamily === null
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {families.map((family) => (
                  <button
                    key={family.id}
                    onClick={() => setSelectedFamily(family.id)}
                    className={`px-4 py-2 rounded-lg border font-medium transition-colors flex items-center gap-2 ${
                      selectedFamily === family.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span>{getIcon(family.icon)}</span>
                    <span>{family.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredProducts.length}</span> operating system{filteredProducts.length !== 1 ? 's' : ''}
              {selectedFamily && (
                <> in <span className="font-semibold text-gray-900">{families.find(f => f.id === selectedFamily)?.name}</span></>
              )}
            </p>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="space-y-8">
            {Object.entries(productsByFamily).map(([familyName, products]) => (
              <div key={familyName}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">
                    {getIcon(families.find(f => f.name === familyName)?.icon || '')}
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900">{familyName}</h2>
                  <span className="text-sm text-gray-500">({products.length})</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductClick(product.id)}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 cursor-pointer transition-all group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{product.manufacturer}</p>
                        </div>
                        <span
                          className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                            product.is_supported
                              ? 'bg-green-100 text-green-700 border border-green-200'
                              : 'bg-red-100 text-red-700 border border-red-200'
                          }`}
                        >
                          {product.is_supported ? 'Supported' : 'EOL'}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Version</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {product.major_version || 'N/A'}
                          </p>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-1">Pattern</p>
                          <p className="text-xs font-mono text-gray-700 truncate">
                            {product.version_pattern || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:gap-2 transition-all">
                        <span>View Details</span>
                        <svg className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No operating systems found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery
                ? `No results match "${searchQuery}". Try adjusting your search.`
                : 'No operating systems available for the selected family.'}
            </p>
            {(searchQuery || selectedFamily) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedFamily(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
